import {HelitecnicaLogo} from "@project/components/branding/helitecnica-logo";
import {Button, Sidebar, useSidebar} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";
import {UNSAFE_PortalProvider} from "@react-aria/overlays";
import {useRouter, useRouterState} from "@tanstack/react-router";
import {useRef} from "react";

import {routes} from "../../../bootstrap/router/routes";
import {appShellMessages} from "../../../messages/app-shell.messages";

import {navigationItems} from "./navigation-items";
import {UserMenu} from "./user-menu";

function isCurrentPath(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function BrandContent() {
  return (
    <>
      <img
        alt="Helitecnica ERP"
        className="hidden size-8 shrink-0 rounded-sm object-contain [.sidebar[data-state=collapsed]_&]:block"
        height={192}
        src="/branding/helitecnica-icon.jpg"
        width={192}
      />
      <span className="[.sidebar[data-state=collapsed]_&]:hidden" data-sidebar="label">
        <HelitecnicaLogo className="block h-12 w-44 shrink-0" />
      </span>
    </>
  );
}

function NavigationContent({presentation}: {presentation: "desktop" | "mobile"}) {
  const {t} = useI18n();
  const pathname = useRouterState({select: (state) => state.location.pathname});
  const router = useRouter();
  const {setMobileOpen} = useSidebar();

  function navigateFromBrand() {
    if (presentation === "mobile") setMobileOpen(false);
    void router.navigate({to: routes.dashboard()});
  }

  return (
    <>
      <Sidebar.Header>
        <Button
          aria-label={t(appShellMessages.goToDashboard)}
          className="h-auto w-full justify-start rounded-2xl px-1 py-2"
          onPress={navigateFromBrand}
          variant="ghost"
        >
          <BrandContent />
        </Button>
      </Sidebar.Header>

      <Sidebar.Content>
        {navigationItems.map((module) => {
          const moduleLabel = t(module.labelDescriptor);

          return (
            <Sidebar.Group key={module.id}>
              <Sidebar.GroupLabel>{moduleLabel}</Sidebar.GroupLabel>
              <Sidebar.Menu aria-label={`${moduleLabel} navigation`}>
                {module.pages.map((page) => {
                  const pageLabel = t(page.labelDescriptor);

                  return (
                    <Sidebar.MenuItem
                      key={page.href}
                      href={page.href}
                      isCurrent={isCurrentPath(pathname, page.href)}
                      textValue={pageLabel}
                      tooltip={pageLabel}
                    >
                      <Sidebar.MenuIcon>{page.icon}</Sidebar.MenuIcon>
                      <Sidebar.MenuLabel>{pageLabel}</Sidebar.MenuLabel>
                    </Sidebar.MenuItem>
                  );
                })}
              </Sidebar.Menu>
            </Sidebar.Group>
          );
        })}
      </Sidebar.Content>

      <Sidebar.Footer>
        <UserMenu presentation={presentation} />
      </Sidebar.Footer>
    </>
  );
}

function MobileNavigationContent() {
  const sheetOverlayRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative flex h-full min-h-0 flex-col" data-mobile-navigation-content="true">
      <UNSAFE_PortalProvider getContainer={() => sheetOverlayRef.current}>
        <NavigationContent presentation="mobile" />
        <div
          ref={sheetOverlayRef}
          className="pointer-events-none absolute inset-0 z-[60] [&>*]:pointer-events-auto"
          data-react-aria-top-layer="true"
          id="mobile-sidebar-sheet-overlay-root"
        />
      </UNSAFE_PortalProvider>
    </div>
  );
}

export function AppNavigationSidebar() {
  return (
    <>
      <Sidebar data-react-aria-top-layer="true">
        <NavigationContent presentation="desktop" />
        <Sidebar.Rail />
      </Sidebar>
      <Sidebar.Mobile className="relative" data-react-aria-top-layer="true">
        <MobileNavigationContent />
      </Sidebar.Mobile>
    </>
  );
}
